<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import QRCode from "qrcode";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import SiteFooter from "../components/SiteFooter.vue";
import { runtimeConfig } from "../runtime-config";
import { useAuthStore } from "../stores/auth";
import { usePetGenerationStore, type ActionStatus } from "../stores/petGeneration";
import type { GenerationVideoClip, PetActionKey } from "../generation-api";

const router = useRouter();
const { t, locale } = useI18n();
const auth = useAuthStore();
const generation = usePetGenerationStore();
const enlargedSource = ref(false);
const paymentQrDataUrl = ref("");
const accountMenuRef = ref<HTMLElement | null>(null);
let paymentPollTimer: number | undefined;
let paymentPollInFlight = false;

watch(
  () => generation.pendingPaymentOrder?.paymentUrl?.trim() ?? "",
  async (url) => {
    if (!url || generation.pendingPaymentOrder?.channel !== "wechat_pay") {
      paymentQrDataUrl.value = "";
      return;
    }
    try {
      paymentQrDataUrl.value = await QRCode.toDataURL(url, { width: 220, margin: 1 });
    } catch {
      paymentQrDataUrl.value = "";
    }
  },
  { immediate: true }
);

watch(
  [
    paymentQrDataUrl,
    () => generation.pendingPaymentOrder?.orderId ?? "",
    () => generation.pendingPaymentOrder?.status ?? ""
  ],
  ([qr, orderId, status]) => {
    if (qr && orderId && status === "pending") {
      startPaymentPolling();
      return;
    }

    stopPaymentPolling();
  },
  { immediate: true }
);
const accountMenuOpen = ref(false);
const directSyncMessage = ref("");

const statusText = computed<Record<ActionStatus, string>>(() => ({
  idle: t("generate.status.idle"),
  queued: t("generate.status.queued"),
  generating: t("generate.status.generating"),
  complete: t("generate.status.complete"),
  error: t("generate.status.error")
}));

const selectedActionSummary = computed(() => generation.selectedActions.map((action) => action.label).join(locale.value === "zh-CN" ? "、" : ", "));
const accountDisplay = computed(() => auth.user?.nickname?.trim() ?? "");

function onFileChange(event: Event) {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  if (file) {
    void generation.setPhoto(file);
  }
}

function onDrop(event: DragEvent) {
  const file = event.dataTransfer?.files?.[0];
  if (file) {
    void generation.setPhoto(file);
  }
}

function openVideo(video?: GenerationVideoClip) {
  if (video) {
    generation.previewVideo = video;
  }
}

function openDesktopDirectSync() {
  const pairingCode = generation.desktopBinding?.pairingCode;
  if (!pairingCode) {
    directSyncMessage.value = t("generate.directSyncNoCode");
    return;
  }

  const params = new URLSearchParams({ code: pairingCode });
  directSyncMessage.value = t("generate.directSyncStarted");
  window.location.href = `autumnpet://sync?${params.toString()}`;
}

function downloadDesktopClient() {
  if (isMacOs()) {
    directSyncMessage.value = t("generate.downloadWindowsOnly");
    return;
  }

  window.open("/client/AutumnPet.zip", "_blank", "noopener,noreferrer");
}

function isMacOs() {
  const nav = navigator as Navigator & { userAgentData?: { platform?: string } };
  const platform = nav.userAgentData?.platform || navigator.platform || navigator.userAgent;
  return /mac/i.test(platform);
}

function formatPaymentAmount(amount: number, currency: string) {
  const normalizedCurrency = currency || (runtimeConfig.market.isInternationalVersion ? "AUD" : "CNY");
  return new Intl.NumberFormat(locale.value, {
    style: "currency",
    currency: normalizedCurrency,
    currencyDisplay: "narrowSymbol"
  }).format(amount);
}

function paymentOrderMessage(message: string) {
  return isPaymentGatewaySetupMessage(message) ? "" : message;
}

function isPaymentGatewaySetupMessage(message: string) {
  return message.includes("支付网关") && message.includes("暂未配置");
}

function toggleAction(key: PetActionKey) {
  generation.toggleAction(key);
}

function onPetSelect(event: Event) {
  const petId = (event.target as HTMLSelectElement).value;
  if (petId) {
    void generation.selectExistingPet(petId);
  }
}

async function logout() {
  accountMenuOpen.value = false;
  auth.logout();
  await router.replace(runtimeConfig.market.loginPath);
}

function closeAccountMenuOnOutsidePointer(event: PointerEvent) {
  if (!accountMenuOpen.value) {
    return;
  }

  const target = event.target;
  if (target instanceof Node && accountMenuRef.value?.contains(target)) {
    return;
  }

  accountMenuOpen.value = false;
}

function startPaymentPolling() {
  if (paymentPollTimer !== undefined) {
    return;
  }

  paymentPollTimer = window.setInterval(() => {
    void pollPendingPaymentSilently();
  }, 3000);
}

function stopPaymentPolling() {
  if (paymentPollTimer === undefined) {
    return;
  }

  window.clearInterval(paymentPollTimer);
  paymentPollTimer = undefined;
}

async function pollPendingPaymentSilently() {
  if (paymentPollInFlight || !generation.pendingPaymentOrder) {
    return;
  }

  paymentPollInFlight = true;
  try {
    await generation.refreshPendingPaymentOrder(true);
  } finally {
    paymentPollInFlight = false;
    if (!generation.pendingPaymentOrder || generation.pendingPaymentOrder.status !== "pending") {
      stopPaymentPolling();
    }
  }
}

onMounted(async () => {
  document.addEventListener("pointerdown", closeAccountMenuOnOutsidePointer);
  await generation.loadActions();
  await generation.refreshAccountState();
});

onBeforeUnmount(() => {
  document.removeEventListener("pointerdown", closeAccountMenuOnOutsidePointer);
  stopPaymentPolling();
});
</script>

<template>
  <main class="app-workbench">
    <header class="topbar">
      <div class="topbar-brand">
        <img class="brand-logo soft" src="/brand-logo.png" alt="" aria-hidden="true" />
        <div>
          <strong>Autumn Pet</strong>
          <span>{{ t("common.console") }}</span>
        </div>
      </div>
      <div class="account-pill">
        <div ref="accountMenuRef" class="account-menu">
          <button class="account-name-button" type="button" @click="accountMenuOpen = !accountMenuOpen">
            <strong>{{ accountDisplay }}</strong>
          </button>
          <div v-if="accountMenuOpen" class="account-dropdown">
            <button type="button" @click="logout">{{ t("generate.logout") }}</button>
          </div>
        </div>
        <button class="quota-button" type="button" @click="generation.openPaymentDialog('credits')">
          {{ t("generate.quota", { count: generation.quota.generationCredits }) }}
        </button>
        <span>{{ t("generate.petCount", { count: generation.quota.petCount, limit: generation.quota.petLimit }) }}</span>
      </div>
    </header>

    <section class="workspace-intro">
      <div>
        <p class="eyebrow">{{ t("generate.eyebrow") }}</p>
        <h1>{{ t("generate.title") }}</h1>
          </div>
      <div class="intro-actions">
        <label v-if="auth.pets.length > 0" class="pet-select">
          <span>{{ t("generate.currentPet") }}</span>
          <select :value="generation.creationMode === 'existing' ? generation.selectedExistingPetId : ''" @change="onPetSelect">
            <option disabled value="">{{ t("generate.choosePet") }}</option>
            <option v-for="pet in auth.pets" :key="pet.petId" :value="pet.petId">
              {{ pet.petName }} · {{ pet.status === "ready" ? t("generate.synced") : pet.status }}
            </option>
          </select>
        </label>
        <button class="secondary-button" type="button" @click="generation.startNewPet">
          {{ t("generate.addPet") }}
        </button>
      </div>
    </section>

    <section class="generator-body">
      <aside class="side-column">
        <section class="panel-card upload-card">
          <div class="section-head">
            <span class="step-badge">1</span>
            <div>
              <h2>{{ t("generate.photoTitle") }}</h2>
              <p>{{ t("generate.photoHint") }}</p>
            </div>
          </div>

          <label class="upload-zone" @dragover.prevent @drop.prevent="onDrop">
            <input type="file" accept="image/png,image/jpeg,image/webp" @change="onFileChange" />
            <span class="upload-symbol" aria-hidden="true">
              <svg viewBox="0 0 32 32" focusable="false">
                <path d="M8.5 7h15a3.5 3.5 0 0 1 3.5 3.5v11a3.5 3.5 0 0 1-3.5 3.5h-15A3.5 3.5 0 0 1 5 21.5v-11A3.5 3.5 0 0 1 8.5 7Z" />
                <path d="M9 20.5 13.4 16l3 3 2.2-2.2 4.4 3.7" />
                <path d="M20.5 12.2h.01" />
                <path d="M16 4.5v7" />
                <path d="m12.8 7.7 3.2-3.2 3.2 3.2" />
              </svg>
            </span>
            <strong>{{ t("generate.choosePhoto") }}</strong>
          </label>

          <button v-if="generation.photoUrl" class="source-preview" type="button" @click="enlargedSource = true">
            <img :src="generation.photoUrl" :alt="t('generate.sourceAlt')" />
            <span>{{ generation.isUploadingPhoto ? t("generate.preparingPhoto") : t("generate.viewSource") }}</span>
          </button>
        </section>

        <section class="panel-card info-card">
          <div class="section-head">
            <span class="step-badge">2</span>
            <div>
              <h2>{{ t("generate.infoTitle") }}</h2>
              <p>{{ generation.isUsingExistingPet ? t("generate.existingInfoHint") : t("generate.newInfoHint") }}</p>
            </div>
          </div>
          <label>
            <span>{{ t("generate.petName") }}</span>
            <input v-model="generation.petName" type="text" :placeholder="t('generate.petNamePlaceholder')" :disabled="generation.isUsingExistingPet" />
          </label>
          <label>
            <span>{{ t("generate.userAlias") }}</span>
            <input v-model="generation.userAlias" type="text" :placeholder="t('generate.userAliasPlaceholder')" :disabled="generation.isUsingExistingPet" />
          </label>
        </section>
      </aside>

      <section class="main-column">
        <section class="panel-card">
          <div class="section-head with-action">
            <div class="section-title-row">
              <span class="step-badge">3</span>
              <div>
                <h2>{{ t("generate.actionTitle") }}</h2>
                <p>{{ t("generate.actionHint") }}</p>
              </div>
            </div>
            <span class="cost-chip">{{ t("generate.estimatedCost", { count: generation.estimatedCreditCost }) }}</span>
          </div>

          <div class="action-list">
            <button
              v-for="action in generation.actions"
              :key="action.key"
              class="action-tile"
              :class="{ selected: generation.selectedActionKeys.includes(action.key), locked: action.locked }"
              type="button"
              :disabled="action.locked || generation.isGenerating"
              @click="toggleAction(action.key)"
            >
              <span class="tile-check" aria-hidden="true"></span>
              <span class="tile-content">
                <strong>{{ action.label }}</strong>
                <small>{{ t("generate.actionCost", { count: action.creditCost ?? 1 }) }}</small>
              </span>
              <span v-if="action.defaultSelected" class="paid-mark included">{{ t("generate.defaultAction") }}</span>
            </button>
          </div>

          <label class="advanced-box">
            <span>
              <strong>{{ t("generate.advancedTitle") }}</strong>
              <small>{{ t("generate.advancedHint") }}</small>
            </span>
            <textarea
              v-model="generation.customDescription"
              maxlength="120"
              :placeholder="t('generate.advancedPlaceholder')"
            ></textarea>
          </label>
        </section>

        <section class="panel-card progress-card">
          <div class="section-head with-action">
            <div class="section-title-row">
              <span class="step-badge">4</span>
              <div>
                <h2>{{ t("generate.progressTitle") }}</h2>
                <p>{{ t("generate.currentActions", { actions: selectedActionSummary || t('generate.noActions') }) }}</p>
              </div>
            </div>
            <button class="primary-button" type="button" :disabled="generation.isUploadingPhoto || generation.isGenerating || generation.isSyncing" @click="generation.generateSelectedActions">
              {{ generation.isUploadingPhoto ? t("generate.compressing") : generation.isGenerating ? t("generate.generating") : t("generate.generate") }}
            </button>
          </div>

          <div class="progress-track" :aria-label="t('generate.progressTitle')">
            <span :style="{ width: `${generation.progress}%` }"></span>
          </div>
          <p class="warm-message">{{ generation.progressMessage }}</p>
          <div v-if="generation.desktopBinding" class="pairing-panel">
            <div class="pairing-content">
              <span>{{ t("generate.pairingTitle") }}</span>
              <strong>{{ generation.desktopBinding.pairingCode }}</strong>
              <small>{{ t("generate.pairingHint") }}</small>
            </div>
            <div class="pairing-actions">
              <button
                class="primary-button"
                type="button"
                @click="openDesktopDirectSync"
              >
                {{ t("generate.directSync") }}
              </button>
              <button class="secondary-button client-download-button" type="button" @click="downloadDesktopClient">
                {{ t("generate.downloadClient") }}
              </button>
            </div>
            <small v-if="directSyncMessage" class="direct-sync-message">{{ directSyncMessage }}</small>
          </div>
          <p v-if="generation.errorMessage" class="form-error">{{ generation.errorMessage }}</p>
        </section>

        <section class="panel-card result-card">
          <div class="section-head with-action">
            <div class="section-title-row">
              <span class="step-badge">5</span>
              <div>
                <h2>{{ t("generate.reviewTitle") }}</h2>
                <p>{{ t("generate.reviewHint") }}</p>
              </div>
            </div>
            <div class="head-actions">
              <button
                class="secondary-button"
                type="button"
                :disabled="!generation.canSync || generation.isSyncing"
                @click="generation.syncDesktopPet"
              >
                {{ generation.isSyncing ? t("generate.syncing") : t("generate.syncDesktop") }}
              </button>
            </div>
          </div>

          <div class="video-list">
            <article v-for="action in generation.selectedActions" :key="action.key" class="video-row">
              <div class="video-meta">
                <strong>{{ action.label }}</strong>
                <span :class="['status-dot', generation.results[action.key].status]">
                  {{ statusText[generation.results[action.key].status] }}
                </span>
              </div>
              <button
                class="video-preview-button"
                type="button"
                :disabled="!generation.results[action.key].video"
                @click="openVideo(generation.results[action.key].video)"
              >
                <video
                  v-if="generation.results[action.key].video"
                  :src="generation.results[action.key].video?.proxyVideoUrl || generation.results[action.key].video?.videoUrl"
                  autoplay
                  muted
                  loop
                  playsinline
                ></video>
                <span v-else>{{ t("generate.waitingVideo") }}</span>
              </button>
              <button
                class="ghost-button"
                type="button"
                :disabled="generation.isGenerating || !generation.results[action.key].video"
                @click="generation.regenerateAction(action.key)"
              >
                {{ generation.results[action.key].regenerateUsed ? t("generate.regenerateAgain") : t("generate.regenerate") }}
              </button>
            </article>
          </div>
        </section>
      </section>
    </section>

    <SiteFooter />

    <div v-if="enlargedSource" class="modal-backdrop" @click.self="enlargedSource = false">
      <div class="image-modal">
        <button class="modal-close" type="button" @click="enlargedSource = false">{{ t("common.close") }}</button>
        <img :src="generation.photoUrl" :alt="t('generate.sourceAlt')" />
      </div>
    </div>

    <div v-if="generation.previewVideo" class="modal-backdrop" @click.self="generation.previewVideo = null">
      <div class="video-modal">
        <button class="modal-close" type="button" @click="generation.previewVideo = null">{{ t("common.close") }}</button>
        <video
          :src="generation.previewVideo.proxyVideoUrl || generation.previewVideo.videoUrl"
          controls
          autoplay
          loop
          playsinline
        ></video>
      </div>
    </div>

    <div v-if="generation.paymentDialog" class="modal-backdrop" @click.self="generation.paymentDialog = null">
      <div class="pay-modal panel-card">
        <h2>{{ generation.paymentDialog.title }}</h2>
        <p>{{ generation.paymentDialog.message }}</p>
        <p v-if="generation.errorMessage" class="form-error payment-error" aria-live="polite">
          {{ generation.errorMessage }}
        </p>
        <div class="product-list">
          <button
            v-for="product in generation.dialogProducts"
            :key="product.productId"
            class="product-option"
            type="button"
            :disabled="generation.isPaymentLoading"
            @click="generation.purchaseProduct(product)"
          >
            <span>
              <strong>{{ product.title }}</strong>
              <small>{{ product.description }}</small>
            </span>
            <em>{{ formatPaymentAmount(product.amountCny, product.currency) }}</em>
          </button>
          <div v-if="generation.pendingPaymentOrder" class="pending-payment">
            <span>{{ t("generate.pendingOrder") }}</span>
            <strong>{{ generation.pendingPaymentOrder.title }}</strong>

            <div v-if="paymentQrDataUrl" class="pay-qr">
              <img :src="paymentQrDataUrl" :alt="t('generate.wechatQrAlt')" width="220" height="220" />
              <small>{{ t("generate.wechatQrHint") }}</small>
              <span class="pay-qr__amount">{{ formatPaymentAmount(generation.pendingPaymentOrder.amountCny, generation.pendingPaymentOrder.currency) }}</span>
            </div>

            <small v-else-if="paymentOrderMessage(generation.pendingPaymentOrder.message)">
              {{ paymentOrderMessage(generation.pendingPaymentOrder.message) }}
            </small>

            <button
              v-if="paymentQrDataUrl"
              class="ghost-button compact"
              type="button"
              :disabled="generation.isPaymentLoading"
              @click="generation.refreshPendingPaymentOrder"
            >
              {{ generation.isPaymentLoading ? t("generate.refreshPaymentLoading") : t("generate.refreshPayment") }}
            </button>
          </div>
          <button
            v-if="generation.canUseCreditForRegenerate"
            class="product-option use-credit"
            type="button"
            :disabled="generation.isGenerating || generation.isPaymentLoading"
            @click="generation.confirmPaidRegenerate"
          >
            <span>
              <strong>{{ t("generate.useCreditTitle") }}</strong>
              <small>{{ t("generate.useCreditHint") }}</small>
            </span>
            <em>{{ t("generate.remainingCredits", { count: generation.quota.generationCredits }) }}</em>
          </button>
          <span v-if="generation.isPaymentLoading" class="payment-loading">{{ t("generate.paymentLoading") }}</span>
          <span
            v-else-if="generation.dialogProducts.length === 0 && !generation.canUseCreditForRegenerate"
            class="payment-loading"
          >
            {{ t("generate.noProducts") }}
          </span>
        </div>
        <div class="modal-actions">
          <button class="secondary-button" type="button" @click="generation.paymentDialog = null">{{ t("common.unused") }}</button>
          <button
            v-if="generation.paymentDialog.intent === 'pet_slot'"
            class="primary-button"
            type="button"
            @click="generation.openPaymentDialog('credits')"
          >
            {{ t("generate.buyCredits") }}
          </button>
        </div>
      </div>
    </div>

  </main>
</template>
